<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref, watch } from 'vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import {
  DATA_REGISTRY_FILTER_FIELDS,
  QUICK_CONTAINS_KEYS,
  optionsForField,
} from '../utils/dataRegistryConsole';

const props = defineProps({
  tab: { type: String, required: true },
  modelValue: { type: Object, required: true },
  sources: { type: Array, default: () => [] },
  facets: { type: Object, default: () => ({}) },
  containsFields: { type: Array, default: () => [] },
  searchHint: { type: String, default: '' },
  supportsContains: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'apply', 'clear', 'advanced']);

const draft = ref({ ...props.modelValue });
let debounceHandle = null;

watch(
  () => props.modelValue,
  value => {
    draft.value = { ...value, contains: [...(value.contains || [])] };
  },
  { deep: true, immediate: true }
);

const fields = computed(() =>
  DATA_REGISTRY_FILTER_FIELDS.filter(
    field =>
      (!field.tabs || field.tabs.includes(props.tab)) && field.key !== 'sourceId'
  ).slice(0, 2)
);

const sourceField = computed(() =>
  DATA_REGISTRY_FILTER_FIELDS.find(field => field.key === 'sourceId')
);

const quickContains = computed(() =>
  props.containsFields.filter(field => QUICK_CONTAINS_KEYS.includes(field.key))
);

const commit = () => {
  emit('update:modelValue', { ...draft.value });
  emit('apply');
};

const onSearchInput = () => {
  if (debounceHandle) clearTimeout(debounceHandle);
  debounceHandle = setTimeout(commit, 450);
};

const onSelectChange = () => {
  if (debounceHandle) clearTimeout(debounceHandle);
  commit();
};

const optionsFor = field =>
  field.key === 'sourceId'
    ? props.sources.map(source => ({
        value: String(source.id),
        label: source.name || source.key || `#${source.id}`,
      }))
    : optionsForField(field, props.facets);

const containsSelected = key => (draft.value.contains || []).includes(key);

const toggleContains = key => {
  const current = draft.value.contains || [];
  draft.value = {
    ...draft.value,
    contains: current.includes(key)
      ? current.filter(item => item !== key)
      : [...current, key],
  };
  commit();
};

const labelForContains = key =>
  props.containsFields.find(field => field.key === key)?.label || key;

const activeChips = computed(() => {
  const selectChips = DATA_REGISTRY_FILTER_FIELDS.filter(
    field =>
      (!field.tabs || field.tabs.includes(props.tab)) &&
      String(props.modelValue[field.key] || '').trim()
  ).map(field => {
    const value = String(props.modelValue[field.key]);
    const option = optionsFor(field).find(item => item.value === value);
    return { key: field.key, label: field.label, value: option?.label || value };
  });

  const containsChips = (props.modelValue.contains || []).map(key => ({
    key: `contains:${key}`,
    label: 'Contém',
    value: labelForContains(key),
  }));

  const manualChips = props.modelValue.sourceKey
    ? [
        {
          key: 'sourceKey',
          label: 'Chave da fonte',
          value: props.modelValue.sourceKey,
        },
      ]
    : [];

  return [...containsChips, ...selectChips, ...manualChips];
});

const removeChip = chipKey => {
  if (chipKey.startsWith('contains:')) {
    const key = chipKey.slice('contains:'.length);
    draft.value = {
      ...draft.value,
      contains: (draft.value.contains || []).filter(item => item !== key),
    };
  } else {
    draft.value = { ...draft.value, [chipKey]: '' };
  }
  commit();
};
</script>

<template>
  <section
    class="flex flex-col gap-3 rounded-lais-sm border border-lais-border-soft bg-white p-3 shadow-sm"
    data-test="data-registry-filter-bar"
  >
    <div class="flex flex-wrap items-end gap-3">
      <label
        class="flex min-w-[220px] flex-1 flex-col gap-1 text-sm font-semibold text-lais-text"
      >
        Busca
        <span class="relative flex items-center">
          <span
            class="i-lucide-search pointer-events-none absolute left-3 size-4 text-lais-text-muted"
            aria-hidden="true"
          />
          <input
            v-model="draft.q"
            class="min-h-10 w-full rounded-lais border border-lais-border pl-9 pr-3 text-sm font-normal"
            type="search"
            :placeholder="searchHint || 'nome, título ou código'"
            data-test="data-registry-search-input"
            @input="onSearchInput"
            @keyup.enter="commit"
          />
        </span>
      </label>

      <label
        v-if="sourceField"
        class="flex min-w-[180px] flex-col gap-1 text-sm font-semibold text-lais-text"
      >
        {{ sourceField.label }}
        <select
          v-model="draft.sourceId"
          class="min-h-10 rounded-lais border border-lais-border px-3 text-sm font-normal"
          data-test="data-registry-filter-sourceId"
          @change="onSelectChange"
        >
          <option value="">Todas</option>
          <option
            v-for="option in optionsFor(sourceField)"
            :key="`sourceId-${option.value}`"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </label>

      <label
        v-for="field in fields"
        :key="field.key"
        class="flex min-w-[150px] flex-col gap-1 text-sm font-semibold text-lais-text"
      >
        {{ field.label }}
        <select
          v-model="draft[field.key]"
          class="min-h-10 rounded-lais border border-lais-border px-3 text-sm font-normal"
          :data-test="`data-registry-filter-${field.key}`"
          @change="onSelectChange"
        >
          <option value="">{{ field.emptyLabel || 'Todos' }}</option>
          <option
            v-for="option in optionsFor(field)"
            :key="`${field.key}-${option.value}`"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </label>

      <LaisPrimaryButton
        icon="i-lucide-sliders-horizontal"
        variant="ghost"
        size="sm"
        data-test="data-registry-open-advanced"
        @click="emit('advanced')"
      >
        Filtros avançados
      </LaisPrimaryButton>
    </div>

    <div
      v-if="supportsContains && quickContains.length"
      class="flex flex-wrap items-center gap-2"
    >
      <span class="text-xs font-semibold uppercase text-lais-text-muted">
        Contém
      </span>
      <button
        v-for="field in quickContains"
        :key="field.key"
        type="button"
        class="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition"
        :class="
          containsSelected(field.key)
            ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
            : 'border-lais-border bg-white text-lais-text-body hover:border-lais-primary'
        "
        :aria-pressed="containsSelected(field.key)"
        :data-test="`data-registry-quick-contains-${field.key}`"
        @click="toggleContains(field.key)"
      >
        <span class="size-3.5" :class="field.icon" aria-hidden="true" />
        {{ field.label }}
      </button>
    </div>

    <div class="flex flex-wrap items-center gap-2">
      <span
        v-for="chip in activeChips"
        :key="chip.key"
        class="inline-flex items-center gap-1 rounded-full bg-lais-primary-50 px-2 py-1 text-xs font-semibold text-lais-primary"
      >
        {{ chip.label }}: {{ chip.value }}
        <button
          type="button"
          class="size-3 i-lucide-x"
          :aria-label="`Remover filtro ${chip.label}`"
          @click="removeChip(chip.key)"
        />
      </span>
      <LaisPrimaryButton
        v-if="activeChips.length"
        icon="i-lucide-eraser"
        variant="ghost"
        size="sm"
        data-test="data-registry-clear-filters"
        @click="emit('clear')"
      >
        Limpar filtros
      </LaisPrimaryButton>
      <span v-else class="text-xs text-lais-text-muted">
        Sem filtros ativos — a busca é aplicada automaticamente.
      </span>
    </div>
  </section>
</template>
